import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Callout } from '@blueprintjs/core';
import { HintButton as Button } from '@/components/base/HintButton';
import { DiscoveryLayout } from '@/discovery/DiscoveryLayout';
import { DiscoveryHeader } from '@/discovery/DiscoveryHeader';
import { useAppModel } from '@/models/AppModel';
import type { ShareMetadata } from './client';
import { useProjectSharing } from './services';

export default function ProjectSharePage() {
  const { id = '' } = useParams();
  const model = useAppModel();
  const [metadata, setMetadata] = useState<ShareMetadata>();
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [savedName, setSavedName] = useState('');
  const controller = useRef<AbortController | undefined>(undefined);
  const locked = useRef(false);
  const { client } = useProjectSharing();
  useEffect(() => {
    const abort = new AbortController();
    setMetadata(undefined);
    setError('');
    setSavedName('');
    void client
      .metadata(id, abort.signal)
      .then((value) => {
        if (!abort.signal.aborted) setMetadata(value);
      })
      .catch((cause) => {
        if (!abort.signal.aborted)
          setError(cause instanceof Error ? cause.message : 'Unable to open this share.');
      });
    return () => {
      abort.abort();
      controller.current?.abort();
    };
  }, [id, client]);
  const openCopy = async () => {
    if (locked.current) return;
    locked.current = true;
    setBusy(true);
    setError('');
    const abort = new AbortController();
    controller.current = abort;
    try {
      const blob = await client.download(id, abort.signal);
      abort.signal.throwIfAborted();
      const name = await model.importSharedProject(blob, abort.signal);
      if (!abort.signal.aborted) setSavedName(name);
    } catch (cause) {
      if (!abort.signal.aborted)
        setError(cause instanceof Error ? cause.message : 'Unable to save a local copy.');
    } finally {
      locked.current = false;
      setBusy(false);
    }
  };
  return (
    <DiscoveryLayout>
      <DiscoveryHeader title="Shared project" actions={null} />
      {error && (
        <Callout intent="danger" role="alert">
          {error}
        </Callout>
      )}
      {!metadata && !error && <p role="status">Loading share…</p>}
      {metadata && (
        <section>
          <h2>{metadata.title}</h2>
          <p>
            {metadata.bytes.toLocaleString()} bytes ·{' '}
            {metadata.expiresAt === null
              ? 'No expiration'
              : `Expires ${new Date(metadata.expiresAt).toUTCString()}`}
          </p>
          <p>
            Open an independent, editable copy on this device. Changes will not affect the
            original or other copies. Simulation starts paused; live extensions are
            disabled for shared copies.
          </p>
          {savedName ? (
            <>
              <p role="status">
                Saved as {savedName}. Your current editor work has not been replaced.
              </p>
              <a
                href={`/editor?project=${encodeURIComponent(savedName)}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open your copy in a new tab
              </a>
            </>
          ) : (
            <Button
              intent="primary"
              loading={busy}
              disabled={busy}
              onClick={() => void openCopy()}
            >
              Open a copy
            </Button>
          )}
          {busy && <p role="status">Downloading and validating project…</p>}
        </section>
      )}
      <p>
        <Link to="/">Your projects</Link>
      </p>
    </DiscoveryLayout>
  );
}
